import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext.tsx";
import AuthLayout from "@/components/auth/AuthLayout";
import { SUPPORT_EMAIL } from "@/components/auth/authCopy";

const ActivateEmail = () => {
    const { activateEmail, isAuthenticated } = useAuth();
    const queryParams = useParams();
    const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
    const [message, setMessage] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        const { uid, token } = queryParams;

        setStatus("loading");

        if (uid && token) {
            activateEmail(uid, token)
                .then(() => {
                    setStatus("success");
                    setMessage("Your email address is confirmed. You can sign in now.");
                })
                .catch((error) => {
                    setStatus("error");
                    setMessage(
                        error.response?.data?.message ||
                            "We couldn't confirm this email. The link may have expired or already been used.",
                    );
                });
        } else {
            setStatus("error");
            setMessage(
                "That activation link isn't complete. Open the link from your email again.",
            );
            console.error("Invalid activation link");
        }
    }, []);

    if (isAuthenticated) {
        return (
            <AuthLayout
                page="activate"
                variant="narrow"
                icon={<Check size={26} strokeWidth={2.5} />}
                title="You're already signed in"
                subtitle="Nothing more to do here — your account is active."
            >
                <button className="auth-btn-primary" type="button" onClick={() => navigate("/")}>
                    Go to your dashboard
                </button>
            </AuthLayout>
        );
    }

    if (status === "loading") {
        return (
            <AuthLayout
                page="activate"
                variant="narrow"
                icon={<Loader2 size={26} strokeWidth={2} className="animate-spin" />}
                iconTone="brand"
                title="Confirming your email"
                subtitle="One moment — we're activating your account."
            >
                <div className="auth-help auth-help--single d-only">
                    Taking too long? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </div>
            </AuthLayout>
        );
    }

    if (status === "success") {
        return (
            <AuthLayout
                page="activate"
                variant="narrow"
                icon={<Check size={26} strokeWidth={2.5} />}
                title="Email confirmed"
                subtitle={message}
            >
                <Link className="auth-btn-primary" to="/login">
                    Go to sign in
                </Link>

                <div className="auth-help auth-help--single d-only">
                    Need a hand? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            page="activate"
            variant="narrow"
            icon={<AlertTriangle size={26} strokeWidth={2} />}
            iconTone="fail"
            title="We couldn't confirm that"
            subtitle={message}
        >
            <Link className="auth-btn-primary" to="/login">
                Go to sign in
            </Link>

            <div className="auth-help">
                <span className="q">Still stuck?</span>
                <span className="a">
                    Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll
                    activate it for you.
                </span>
            </div>
        </AuthLayout>
    );
};

export default ActivateEmail;
